import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Textarea,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import {
  adminCreateProjectIqCapability,
  adminUpdateProjectIqCapability,
} from "apps/project-iq/api";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import EmojiPicker from "emoji-picker-react";
import { useEffect, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { useManageIqCapabilitiesStyles } from "./ManageIqCapabilities.styles";
import { IEditCapabilityDialogProps } from "./ManageIqCapabilities.types";

export const EditCapabilityDialog = ({
  open,
  capability,
  existingTitles,
  onSave,
  onDismiss,
}: IEditCapabilityDialogProps): JSX.Element => {
  const styles = useManageIqCapabilitiesStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();

  const [title, setTitle] = useState<string>("");
  const [shortDescription, setShortDescription] = useState<string>("");
  const [icon, setIcon] = useState<string>("");
  const [url, setUrl] = useState<string>("");
  const [enabled, setEnabled] = useState<boolean>(true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);

  useEffect(() => {
    if (capability) {
      setTitle(capability.title);
      setShortDescription(capability.shortDescription);
      setIcon(capability.icon);
      setUrl(capability.url ?? "");
      setEnabled(capability.enabled);
      setFieldErrors({});
    } else {
      setTitle("");
      setShortDescription("");
      setIcon("");
      setUrl("");
      setEnabled(true);
      setFieldErrors({});
    }
    setIsSaving(false);
    setIsEmojiPickerOpen(false);
  }, [capability, open]);

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!title.trim()) errors.title = "Title is required";
    if (
      existingTitles.some(
        (t) => t.trim().toLowerCase() === title.trim().toLowerCase(),
      )
    ) {
      errors.title = "A capability with this title already exists";
    }
    if (!shortDescription.trim())
      errors.shortDescription = "Short description is required";
    if (!icon.trim()) errors.icon = "Icon is required";
    return errors;
  };

  const handleSave = async (): Promise<void> => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setIsSaving(true);
    try {
      const data = {
        title,
        shortDescription,
        icon,
        url: url || undefined,
        enabled,
      };
      if (capability?.id) {
        await adminUpdateProjectIqCapability(capability.id, data);
      } else {
        await adminCreateProjectIqCapability(data);
      }
      onSave();
    } catch {
      setFieldErrors((prev) => ({
        ...prev,
        general: "Save failed. Please try again.",
      }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) onDismiss();
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
              />
            }
          >
            {capability ? "Edit Capability" : "Add Capability"}
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              {isSaving && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving capability..." />
                </div>
              )}
              {fieldErrors.general && (
                <div role="alert" className={styles.errorAlert}>
                  {fieldErrors.general}
                </div>
              )}
              <Field
                label="Title"
                required
                validationState={fieldErrors.title ? "error" : "none"}
                validationMessage={fieldErrors.title}
                className={styles.dialogField}
              >
                <Input
                  value={title}
                  onChange={(_, d) => {
                    setTitle(d.value);
                    if (fieldErrors.title)
                      setFieldErrors((prev) => ({ ...prev, title: "" }));
                  }}
                  placeholder="Capability title"
                />
              </Field>
              <Field
                label="Short Description"
                required
                validationState={
                  fieldErrors.shortDescription ? "error" : "none"
                }
                validationMessage={fieldErrors.shortDescription}
                className={styles.dialogField}
              >
                <Textarea
                  value={shortDescription}
                  onChange={(_, d) => {
                    setShortDescription(d.value);
                    if (fieldErrors.shortDescription)
                      setFieldErrors((prev) => ({
                        ...prev,
                        shortDescription: "",
                      }));
                  }}
                  placeholder="Brief description"
                />
              </Field>
              <Field
                label="Icon (emoji)"
                required
                validationState={fieldErrors.icon ? "error" : "none"}
                validationMessage={fieldErrors.icon}
                className={styles.dialogField}
              >
                <div className={styles.iconPickerRow}>
                  <Popover
                    open={isEmojiPickerOpen}
                    onOpenChange={(_, d) => setIsEmojiPickerOpen(d.open)}
                  >
                    <PopoverTrigger disableButtonEnhancement>
                      <Button
                        className={styles.emojiPickerButton}
                        aria-label="Pick emoji"
                        appearance="outline"
                      >
                        {icon || "😀"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverSurface className={styles.emojiPopoverSurface}>
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          setIcon(emojiData.emoji);
                          if (fieldErrors.icon)
                            setFieldErrors((prev) => ({ ...prev, icon: "" }));
                          setIsEmojiPickerOpen(false);
                        }}
                        autoFocusSearch
                        width={300}
                        height={350}
                      />
                    </PopoverSurface>
                  </Popover>
                  <Input
                    value={icon}
                    onChange={(_, d) => {
                      setIcon(d.value);
                      if (fieldErrors.icon)
                        setFieldErrors((prev) => ({ ...prev, icon: "" }));
                    }}
                    placeholder="or type emoji"
                    className={styles.iconTextInput}
                  />
                </div>
              </Field>
              <Field label="URL (optional)" className={styles.dialogField}>
                <Input
                  value={url}
                  onChange={(_, d) => setUrl(d.value)}
                  placeholder="https://..."
                />
              </Field>
              <CustomToggle
                checked={enabled}
                onChange={setEnabled}
                label="Enabled"
              />
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            tooltip="Save"
            dimmed={!title.trim() || !shortDescription.trim() || !icon.trim()}
            disabled={isSaving}
            onClick={() => void handleSave()}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
